/**
 * AuditIQ Sports — Public marketing pages
 * Landing + per-sport pages + Health IQ + Compliance + Demo + CRM intake
 */
import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Layout from "../../components/Layout";
import SEOHead from "../../components/SEOHead";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Toaster } from "../../components/ui/sonner";
import { toast } from "sonner";
import { sportsApi } from "../../lib/sportsApi";
import { fetchDemoCredentials } from "../../lib/demoCredentials";
import {
  Trophy, Activity, ShieldCheck, BarChart3, Users2, Target,
  Heart, Brain, GraduationCap, Briefcase, AlertTriangle, Crown,
  ArrowRight, CheckCircle2, Database, ScanLine, Award, Clipboard,
  Dumbbell, ChevronDown, Globe, FileText, Stethoscope,
} from "lucide-react";

const SPORT_META = {
  football:   { Icon: Trophy,   color: "gold",     stats: ["Snaps","Pass Yds","Rush Yds","Rec Yds","Tackles","Sacks","INTs","Practice Grade","Game Grade","Health IQ"] },
  basketball: { Icon: Activity, color: "electric", stats: ["Minutes","PPG","RPG","APG","SPG","BPG","FG%","3PT%","FT%","+/-","Practice Grade","Health IQ"] },
  baseball:   { Icon: Target,   color: "gold",     stats: ["AVG","OBP","SLG","OPS","RBI","Runs","ERA","WHIP","K","BB","Field %","Health IQ"] },
  golf:       { Icon: Award,    color: "electric", stats: ["Avg Score","Fairways %","GIR %","Putts/Round","Scrambling","Sand Save","Drive Dist","Practice Grade","Health IQ"] },
};

// =====================================================================
// LANDING — /auditiq-sports
// =====================================================================
export const SportsLanding = () => {
  const [meta, setMeta] = useState(null);
  const [universities, setUniversities] = useState([]);

  useEffect(() => {
    sportsApi.meta().then(setMeta).catch(() => {});
    sportsApi.universities().then(d => setUniversities(d.universities || [])).catch(() => {});
  }, []);

  return (
    <Layout>
      <SEOHead
        title="AuditIQ Sports Intelligence + Health IQ | University Athletics Platform"
        description="Athletics intelligence for universities — football, basketball, baseball, golf, recruiting, Health IQ readiness monitoring, compliance, and AI-powered public-data crawling."
        path="/auditiq-sports" />
      <Toaster position="top-right" richColors theme="dark" />

      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="sports-hero">
        <div className="absolute inset-0 cyber-grid opacity-30" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_85%,rgba(45,156,219,0.18),transparent_55%),radial-gradient(circle_at_85%_15%,rgba(201,168,76,0.14),transparent_55%)]" />
        <div className="container-x relative section-y">
          <div className="text-[11px] uppercase tracking-[0.28em] text-electric flex items-center gap-2">
            <Trophy size={12} /> AuditIQ Sports + Health IQ
          </div>
          <h1 className="font-display mt-4 max-w-4xl">Athletics intelligence for universities, coaches, and health staff.</h1>
          <p className="text-cream-dim mt-5 max-w-2xl">
            One unified platform for football, basketball, baseball, golf, recruiting,
            performance, Health IQ readiness, and compliance — built on the AuditIQ
            integrity standard.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 btn-stack">
            <Link to="/auditiq-sports/demo" className="contents">
              <Button className="btn-gold h-12" data-testid="sports-demo-cta">
                See Demo Mode <ArrowRight size={14} className="ml-2" />
              </Button>
            </Link>
            <Link to="/auditiq-sports/healthiq" className="contents">
              <Button className="btn-ghost-gold h-12" data-testid="sports-healthiq-cta">
                Health IQ Overview
              </Button>
            </Link>
            <a href="#sports-demo-form" className="contents">
              <Button className="btn-ghost-gold h-12" data-testid="sports-request-cta">Request Pilot</Button>
            </a>
          </div>
          <div className="mt-10 flex flex-wrap gap-x-8 gap-y-2 text-[11px] uppercase tracking-[0.2em] text-steel">
            <span className="flex items-center gap-2"><ShieldCheck size={12} /> FERPA-aware</span>
            <span className="flex items-center gap-2"><Stethoscope size={12} /> Health IQ wellness</span>
            <span className="flex items-center gap-2"><Globe size={12} /> Robots.txt compliant crawler</span>
            <span className="flex items-center gap-2"><CheckCircle2 size={12} /> Audit-logged actions</span>
          </div>
        </div>
      </section>

      {/* 4 SPORTS GRID */}
      <section className="bg-navy-deep border-y border-gold-dim/20 section-y">
        <div className="container-x">
          <div className="text-[11px] uppercase tracking-[0.28em] text-electric">Sport Modules</div>
          <h2 className="font-display mt-3">Four sports. One intelligence layer.</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8" data-testid="sports-grid">
            {Object.entries(SPORT_META).map(([sport, m]) => (
              <Link key={sport} to={`/auditiq-sports/${sport}`}
                    className="card-premium glass hover:border-gold transition-colors block"
                    data-testid={`sport-card-${sport}`}>
                <m.Icon size={28} className={`text-${m.color}`} />
                <h3 className="font-display mt-4 capitalize">{sport} IQ</h3>
                <p className="text-cream-dim text-sm mt-2">
                  Roster · stats · depth chart · Health IQ · recruiting · reports.
                </p>
                <div className="text-electric text-xs mt-4 flex items-center gap-1">
                  Open module <ArrowRight size={12} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 6 INTEL ENGINES */}
      <section className="container-x section-y">
        <div className="grid lg:grid-cols-3 gap-4">
          {[
            { Icon: Heart,        title: "Health IQ Readiness",   blurb: "Daily wellness, recovery, and load monitoring with green/yellow/orange/red readiness colors." },
            { Icon: Users2,       title: "Recruiting Pipeline",   blurb: "Prospect tracking, AI fit scoring, offer/visit/commit funnel, transfer tracking." },
            { Icon: BarChart3,    title: "Performance Analytics", blurb: "Sport-specific stat dashboards with practice + game grade trends." },
            { Icon: ScanLine,     title: "Public Data Crawler",   blurb: "Robots.txt-compliant collection of public rosters, schedules, scores, and recruiting boards." },
            { Icon: Brain,        title: "AthleteIdentityResolver", blurb: "Match athletes across sources at 90+ confidence; 70-89 routes to admin approval queue." },
            { Icon: ShieldCheck,  title: "Compliance Layer",      blurb: "Eligibility, recruiting logs, scholarship records, code-of-conduct, and audit logging." },
          ].map((c, i) => (
            <div key={i} className="card-premium glass" data-testid={`intel-engine-${i}`}>
              <c.Icon size={22} className="text-gold" />
              <h3 className="font-display mt-3">{c.title}</h3>
              <p className="text-cream-dim text-sm mt-2">{c.blurb}</p>
            </div>
          ))}
        </div>
      </section>

      {/* DEMO UNIVERSITIES */}
      {universities.length > 0 && (
        <section className="bg-navy-deep border-y border-gold-dim/20 section-y">
          <div className="container-x">
            <div className="text-[11px] uppercase tracking-[0.28em] text-electric">Demo Mode</div>
            <h2 className="font-display mt-3">10 Florida universities seeded for demo</h2>
            <p className="text-cream-dim mt-3 max-w-2xl">
              Every demo dashboard is populated with public-style athletics data
              — explore every dashboard with your own demo account.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 mt-8" data-testid="demo-universities">
              {universities.map(u => (
                <div key={u.id} className="glass p-4">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-steel">{u.level}</div>
                  <div className="font-display text-cream mt-1">{u.name}</div>
                  <div className="text-electric text-xs mt-2">{u.mascot} · {u.conference}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* PILOT REQUEST */}
      <DemoRequestForm />

      {/* COMPLIANCE NOTE */}
      {meta && (
        <section className="container-x pb-16">
          <div className="card-premium glass-accent flex items-start gap-3 max-w-3xl">
            <ShieldCheck size={18} className="text-electric mt-1 shrink-0" />
            <div className="text-cream-dim text-sm">
              <span className="text-electric font-medium">Compliance:</span>{" "}
              {meta.compliance_disclaimer}
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
};

// =====================================================================
// DEMO REQUEST FORM (used on landing + sport pages)
// =====================================================================
export const DemoRequestForm = () => {
  const [form, setForm] = useState({
    contact_name: "", email: "", phone: "", organization: "", role: "", sport_interests: [], message: "",
  });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const r = await sportsApi.demoRequest(form);
      toast.success(r.message || "Submitted");
      setForm({ contact_name: "", email: "", phone: "", organization: "", role: "", sport_interests: [], message: "" });
    } catch (err) { toast.error("Submit failed"); }
  };
  const toggleSport = (s) => setForm(f => ({
    ...f, sport_interests: f.sport_interests.includes(s)
      ? f.sport_interests.filter(x => x !== s) : [...f.sport_interests, s]
  }));
  return (
    <section id="sports-demo-form" className="container-x section-y">
      <div className="card-premium glass max-w-3xl mx-auto" data-testid="sports-demo-form">
        <div className="text-[11px] uppercase tracking-[0.28em] text-gold flex items-center gap-2">
          <Briefcase size={12} /> Pilot Request
        </div>
        <h2 className="font-display mt-3">Request an AuditIQ Sports demo</h2>
        <form onSubmit={submit} className="grid md:grid-cols-2 gap-3 mt-6">
          <Input required placeholder="Contact name" value={form.contact_name}
                 onChange={e => setForm({ ...form, contact_name: e.target.value })}
                 className="bg-navy-deep border-gold-dim/40" data-testid="sports-form-name" />
          <Input required type="email" placeholder="Email" value={form.email}
                 onChange={e => setForm({ ...form, email: e.target.value })}
                 className="bg-navy-deep border-gold-dim/40" data-testid="sports-form-email" />
          <Input placeholder="Phone" value={form.phone}
                 onChange={e => setForm({ ...form, phone: e.target.value })}
                 className="bg-navy-deep border-gold-dim/40" />
          <Input required placeholder="Organization / University" value={form.organization}
                 onChange={e => setForm({ ...form, organization: e.target.value })}
                 className="bg-navy-deep border-gold-dim/40" data-testid="sports-form-org" />
          <Input placeholder="Your role (Athletic Director, Head Coach, ...)" value={form.role}
                 onChange={e => setForm({ ...form, role: e.target.value })}
                 className="bg-navy-deep border-gold-dim/40 md:col-span-2" />
          <div className="md:col-span-2">
            <div className="text-[10px] uppercase tracking-[0.22em] text-steel mb-2">Sports of interest</div>
            <div className="flex flex-wrap gap-2">
              {Object.keys(SPORT_META).map(s => (
                <button key={s} type="button" onClick={() => toggleSport(s)}
                        className={`px-3 py-1.5 rounded border text-xs uppercase tracking-[0.18em] ${
                          form.sport_interests.includes(s)
                            ? "border-gold text-gold bg-gold/10"
                            : "border-gold-dim/40 text-cream-dim hover:text-gold"
                        }`}
                        data-testid={`sports-form-pill-${s}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
          <Textarea rows={3} placeholder="What would you like to evaluate?"
                    value={form.message}
                    onChange={e => setForm({ ...form, message: e.target.value })}
                    className="bg-navy-deep border-gold-dim/40 text-cream md:col-span-2" />
          <div className="md:col-span-2 flex justify-end">
            <Button type="submit" className="btn-gold h-12" data-testid="sports-form-submit">
              Submit pilot request <ArrowRight size={14} className="ml-2" />
            </Button>
          </div>
        </form>
      </div>
    </section>
  );
};

// =====================================================================
// SPORT PAGE — /auditiq-sports/:sport
// =====================================================================
export const SportPage = () => {
  const { sport } = useParams();
  const m = SPORT_META[sport];
  const [teams, setTeams] = useState([]);
  const [stats, setStats] = useState([]);

  useEffect(() => {
    if (!m) return;
    sportsApi.teams({ sport }).then(d => setTeams(d.teams || []));
    sportsApi.sportStats(sport, { limit: 12 }).then(d => setStats(d.stats || []));
  }, [sport, m]);

  if (!m) {
    return <Layout><section className="container-x section-y">
      <h1 className="font-display">Sport not found</h1>
      <Link to="/auditiq-sports" className="text-electric mt-4 inline-block">Back to Sports</Link>
    </section></Layout>;
  }

  const Title = sport.charAt(0).toUpperCase() + sport.slice(1);

  return (
    <Layout>
      <SEOHead title={`${Title} IQ | AuditIQ Sports`}
               description={`${Title} performance analytics, roster management, Health IQ readiness, recruiting, and reports for university athletics.`}
               path={`/auditiq-sports/${sport}`} />
      <Toaster position="top-right" richColors theme="dark" />
      <section className="container-x section-y">
        <div className="text-[11px] uppercase tracking-[0.28em] text-electric flex items-center gap-2">
          <m.Icon size={12} /> {Title} Module
        </div>
        <h1 className="font-display mt-4">{Title} IQ</h1>
        <p className="text-cream-dim mt-4 max-w-2xl">
          Track every {sport} roster, stats line, practice grade, Health IQ readiness,
          and recruiting prospect across your athletics program.
        </p>
      </section>

      <section className="bg-navy-deep border-y border-gold-dim/20 section-y">
        <div className="container-x">
          <div className="text-[11px] uppercase tracking-[0.28em] text-electric">Tracked metrics</div>
          <h2 className="font-display mt-3">{Title} stat coverage</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-6" data-testid={`${sport}-metrics-grid`}>
            {m.stats.map((s, i) => (
              <div key={s} className="glass p-4" data-testid={`${sport}-metric-${i}`}>
                <CheckCircle2 size={14} className="text-electric" />
                <div className="text-cream mt-2 text-sm">{s}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {teams.length > 0 && (
        <section className="container-x section-y">
          <div className="text-[11px] uppercase tracking-[0.28em] text-electric">Teams in demo</div>
          <h2 className="font-display mt-3">{teams.length} {Title} teams</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-6" data-testid={`${sport}-teams-grid`}>
            {teams.map(t => (
              <div key={t.id} className="glass p-4">
                <div className="text-[10px] uppercase tracking-[0.22em] text-gold">{t.season}</div>
                <div className="font-display text-cream mt-1">{t.name}</div>
                <div className="text-electric text-xs mt-2">{t.head_coach}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {stats.length > 0 && (
        <section className="bg-navy-deep border-y border-gold-dim/20 section-y">
          <div className="container-x">
            <div className="text-[11px] uppercase tracking-[0.28em] text-electric">Sample stat lines</div>
            <h2 className="font-display mt-3">Live demo data</h2>
            <div className="table-wrap glass mt-4">
              <table className="w-full text-sm">
                <thead className="border-b border-gold-dim/20">
                  <tr className="text-cream-dim text-xs uppercase tracking-[0.18em]">
                    <th className="text-left p-3">Athlete ID</th>
                    <th className="text-right p-3">Games</th>
                    <th className="text-right p-3">Practice Grade</th>
                    <th className="text-right p-3">Game Grade</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.slice(0, 10).map(s => (
                    <tr key={s.athlete_id} className="border-b border-gold-dim/10">
                      <td className="p-3 text-cream font-mono text-xs">{s.athlete_id}</td>
                      <td className="p-3 text-right text-cream">{s.games_played}</td>
                      <td className="p-3 text-right text-cream">{s.practice_grade}</td>
                      <td className="p-3 text-right text-cream">{s.game_grade}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      <DemoRequestForm />
    </Layout>
  );
};

// =====================================================================
// HEALTH IQ — /auditiq-sports/healthiq
// =====================================================================
export const HealthIQPage = () => {
  const [meta, setMeta] = useState(null);
  useEffect(() => { sportsApi.meta().then(setMeta).catch(() => {}); }, []);
  return (
    <Layout>
      <SEOHead title="Health IQ — Wellness Readiness for Athletes | AuditIQ Sports"
               description="Health IQ is the wellness, readiness, recovery, and performance-monitoring layer inside AuditIQ Sports. Daily check-ins, training load, return-to-play, trainer review."
               path="/auditiq-sports/healthiq" />
      <section className="container-x section-y">
        <div className="text-[11px] uppercase tracking-[0.28em] text-electric flex items-center gap-2">
          <Heart size={12} /> Wellness Readiness Layer
        </div>
        <h1 className="font-display mt-4">Health IQ</h1>
        <p className="text-cream-dim mt-4 max-w-2xl">
          Health IQ is the wellness-readiness layer inside AuditIQ Sports. It helps coaches
          and athletic departments monitor athlete recovery, readiness, performance trends,
          and health-related risk signals — without replacing licensed medical judgment.
        </p>
        {meta && (
          <div className="card-premium glass-accent flex items-start gap-3 mt-8 max-w-3xl" data-testid="healthiq-disclaimer">
            <AlertTriangle size={18} className="text-electric mt-1 shrink-0" />
            <div className="text-cream-dim text-sm">
              <span className="text-electric font-medium">Medical disclaimer:</span> {meta.health_iq_disclaimer}
            </div>
          </div>
        )}
      </section>

      <section className="bg-navy-deep border-y border-gold-dim/20 section-y">
        <div className="container-x">
          <div className="text-[11px] uppercase tracking-[0.28em] text-electric">Daily check-in</div>
          <h2 className="font-display mt-3">What Health IQ tracks</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-6">
            {[
              "Sleep hours","Hydration","Energy","Mood","Stress","Soreness","Pain flags",
              "Recovery status","Training load","Practice load","Game load","Travel load",
              "Return-to-play status","Medical clearance","Trainer notes",
            ].map((t, i) => (
              <div key={t} className="glass p-3 flex items-center gap-3" data-testid={`hiq-metric-${i}`}>
                <CheckCircle2 size={14} className="text-electric shrink-0" />
                <span className="text-cream text-sm">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="container-x section-y">
        <div className="text-[11px] uppercase tracking-[0.28em] text-electric">Readiness colors</div>
        <h2 className="font-display mt-3">Readiness signal</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-6">
          {[
            { color: "var(--auditiq-success)", name: "Green", action: "Ready" },
            { color: "var(--auditiq-warning)", name: "Yellow", action: "Monitor" },
            { color: "#F59E0B",                 name: "Orange", action: "Limited activity recommended" },
            { color: "var(--auditiq-critical)", name: "Red", action: "Hold — review by qualified health staff" },
          ].map(c => (
            <div key={c.name} className="card-premium glass" data-testid={`hiq-color-${c.name.toLowerCase()}`}>
              <div className="w-8 h-8 rounded-full" style={{ background: c.color }} />
              <div className="font-display text-cream mt-3">{c.name}</div>
              <div className="text-cream-dim text-sm mt-1">{c.action}</div>
            </div>
          ))}
        </div>
      </section>

      <DemoRequestForm />
    </Layout>
  );
};

// =====================================================================
// COMPLIANCE — /auditiq-sports/compliance
// =====================================================================
export const SportsCompliancePage = () => {
  const [meta, setMeta] = useState(null);
  useEffect(() => { sportsApi.meta().then(setMeta).catch(() => {}); }, []);
  return (
    <Layout>
      <SEOHead title="Compliance & Audit | AuditIQ Sports"
               description="Role-based access, FERPA-aware data handling, audit logging, and compliance enforcement for AuditIQ Sports."
               path="/auditiq-sports/compliance" />
      <section className="container-x section-y">
        <div className="text-[11px] uppercase tracking-[0.28em] text-electric flex items-center gap-2">
          <ShieldCheck size={12} /> Compliance + Audit
        </div>
        <h1 className="font-display mt-4">Compliance enforcement, by design</h1>
        <p className="text-cream-dim mt-4 max-w-2xl">
          Every endpoint enforces role-based access. Health data is invisible to non-health
          roles. Athlete profiles never auto-merge below 90% confidence. Every sensitive
          action is audit-logged.
        </p>
      </section>

      <section className="bg-navy-deep border-y border-gold-dim/20 section-y">
        <div className="container-x">
          <h2 className="font-display">14 distinct roles. One permission matrix.</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-6" data-testid="compliance-roles-grid">
            {meta && Object.entries(meta.roles).map(([key, r]) => (
              <div key={key} className="glass p-4" data-testid={`role-${key}`}>
                <div className="text-[10px] uppercase tracking-[0.22em] text-gold">{r.scope}</div>
                <div className="font-display text-cream mt-1">{r.label}</div>
                <div className="text-cream-dim text-xs mt-2">{(meta.permissions[key] || []).length} permissions</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="container-x section-y">
        <div className="grid lg:grid-cols-2 gap-4">
          <div className="card-premium glass">
            <h3 className="font-display">Enforced rules</h3>
            <ul className="mt-3 space-y-2 text-cream-dim text-sm">
              {[
                "No private medical data visible to non-health roles",
                "No private academic detail visible to sponsor viewers",
                "No parent access without explicit permission",
                "No crawler collection from login-only or private sources",
                "No auto athlete-profile merge below 90 confidence",
                "All compliance-sensitive actions audit-logged",
              ].map((r, i) => (
                <li key={i} className="flex items-start gap-2">
                  <CheckCircle2 size={14} className="text-electric mt-1 shrink-0" />{r}
                </li>
              ))}
            </ul>
          </div>
          <div className="card-premium glass">
            <h3 className="font-display">Disclaimer</h3>
            <p className="text-cream-dim text-sm mt-3">{meta?.compliance_disclaimer}</p>
            <p className="text-cream-dim text-sm mt-3">{meta?.health_iq_disclaimer}</p>
          </div>
        </div>
      </section>
    </Layout>
  );
};

// =====================================================================
// DEMO — /auditiq-sports/demo
// =====================================================================
export const SportsDemoPage = () => {
  const [universities, setUniversities] = useState([]);
  const [sources, setSources] = useState([]);
  const [queue, setQueue] = useState([]);
  const [meta, setMeta] = useState(null);
  const [demoPwd, setDemoPwd] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    sportsApi.meta().then(setMeta).catch(() => {});
    sportsApi.universities().then(d => setUniversities(d.universities || []));
    fetchDemoCredentials().then(d => setDemoPwd(d?.sports?.password || ""));
    // Only attempt admin-only endpoints if the user has a token
    const token = typeof window !== "undefined" && localStorage.getItem("auditiq_token");
    if (token) {
      sportsApi.crawlerSources().then(d => setSources(d.sources || [])).catch(() => {});
      sportsApi.matchQueue().then(d => setQueue(d.queue || [])).catch(() => {});
    }
  }, []);

  return (
    <Layout>
      <SEOHead title="Demo Mode | AuditIQ Sports"
               description="AuditIQ Sports demo — public data crawling simulation across 10 Florida universities. Live integrations available in production."
               path="/auditiq-sports/demo" />
      <section className="container-x section-y">
        <div className="text-[11px] uppercase tracking-[0.28em] text-electric flex items-center gap-2">
          <Database size={12} /> Demo Mode
        </div>
        <h1 className="font-display mt-4">Demo Mode — Public data crawling simulation</h1>
        <p className="text-cream-dim mt-4 max-w-2xl">
          Live integrations available in production. The demo dataset spans 10 Florida
          universities, 4 sports, and 80 athletes — wired to every dashboard role.
        </p>
        <div className="mt-6">
          <Link to="/auditiq-sports/command-center" className="contents">
            <Button className="btn-gold h-12" data-testid="open-command-center">
              <Crown size={14} className="mr-2" /> Open Command Center
            </Button>
          </Link>
        </div>
        <div className="card-premium glass-accent flex items-start gap-3 mt-8 max-w-3xl">
          <ShieldCheck size={18} className="text-electric mt-1 shrink-0" />
          <div className="text-cream-dim text-sm">
            All crawled records below are deterministic mocks. No live websites are touched.
            Toggle <span className="text-electric font-mono text-xs">ENABLE_LIVE_CRAWLING=true</span> in
            backend env when you're ready for a real pilot source.
          </div>
        </div>
      </section>

      <section className="bg-navy-deep border-y border-gold-dim/20 section-y">
        <div className="container-x">
          <div className="text-[11px] uppercase tracking-[0.28em] text-electric">Demo universities</div>
          <h2 className="font-display mt-3">10 Florida universities · 40 teams · 80 athletes</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 mt-6" data-testid="demo-universities-grid">
            {universities.map(u => (
              <Link key={u.id} to={`/auditiq-sports/dashboard/athletic-director?u=${u.id}`}
                    className="glass p-4 hover:border-gold transition-colors block">
                <div className="text-[10px] uppercase tracking-[0.22em] text-gold">{u.level}</div>
                <div className="font-display text-cream mt-1">{u.name}</div>
                <div className="text-electric text-xs mt-2">{u.mascot} · {u.conference}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="container-x section-y grid lg:grid-cols-2 gap-6">
        <div className="card-premium glass" data-testid="demo-sources-card">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-gold">
            <Globe size={12} /> Public data sources
          </div>
          <h3 className="font-display mt-2">{sources.length || 8} sources seeded</h3>
          <ul className="mt-4 space-y-2 text-cream-dim text-sm max-h-60 overflow-y-auto">
            {sources.slice(0, 8).map(s => (
              <li key={s.id} className="flex items-center justify-between border-b border-gold-dim/10 pb-2">
                <span>{s.name}</span>
                <span className="text-electric text-xs">{s.crawl_frequency}</span>
              </li>
            ))}
            {sources.length === 0 && <li className="text-cream-dim text-xs">Sign in as a super admin to see source list.</li>}
          </ul>
          <Link to="/portal/login" className="text-electric text-sm mt-4 inline-flex items-center gap-1">
            Open admin to run crawler <ArrowRight size={12} />
          </Link>
        </div>
        <div className="card-premium glass" data-testid="demo-match-queue-card">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-gold">
            <Brain size={12} /> Match approval queue
          </div>
          <h3 className="font-display mt-2">{queue.length || 6} pending candidates</h3>
          <p className="text-cream-dim text-sm mt-2">
            Athletes matched at 70-89% confidence require human approval. 90+ auto-merge.
            Below 70 stays unresolved.
          </p>
          <Link to="/auditiq-sports/admin" className="text-electric text-sm mt-4 inline-flex items-center gap-1">
            Open match queue <ArrowRight size={12} />
          </Link>
        </div>
      </section>

      <section className="bg-navy-deep border-y border-gold-dim/20 section-y">
        <div className="container-x">
          <div className="text-[11px] uppercase tracking-[0.28em] text-electric">Try a dashboard</div>
          <h2 className="font-display mt-3">Sign in by role</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-6" data-testid="dashboard-role-grid">
            {[
              { role: "athletic-director", label: "Athletic Director", email: "ad@fsu.demo", Icon: Crown },
              { role: "head-coach",        label: "Head Coach",        email: "headcoach@fsu.demo", Icon: Trophy },
              { role: "trainer",           label: "Trainer / Health",  email: "trainer@fsu.demo", Icon: Stethoscope },
              { role: "compliance",        label: "Compliance Officer",email: "compliance@fsu.demo", Icon: ShieldCheck },
              { role: "advisor",           label: "Academic Advisor",  email: "advisor@fsu.demo", Icon: GraduationCap },
              { role: "recruiting",        label: "Recruiting Coord.", email: "recruit@fsu.demo", Icon: Users2 },
              { role: "athlete",           label: "Athlete",           email: "athlete@fsu.demo", Icon: Activity },
              { role: "guardian",          label: "Parent / Guardian", email: "guardian@fsu.demo", Icon: Heart },
              { role: "sponsor",           label: "Sponsor Viewer",    email: "sponsor@fsu.demo", Icon: Award },
              { role: "auditor",           label: "Read-Only Auditor", email: "auditor.sports@auditiq.ai", Icon: Clipboard },
            ].map(r => (
              <Link key={r.role} to={`/auditiq-sports/dashboard/${r.role}`}
                    className="glass p-4 hover:border-gold transition-colors block"
                    data-testid={`role-card-${r.role}`}>
                <r.Icon size={18} className="text-gold" />
                <div className="font-display text-cream mt-3">{r.label}</div>
                <div className="text-cream-dim text-xs mt-1 font-mono">{r.email}</div>
                <div className="text-cream-dim text-xs mt-1">password: {demoPwd || "—"}</div>
              </Link>
            ))}
          </div>
          <div className="mt-6">
            <Button onClick={() => nav("/portal/login")} className="btn-gold h-12" data-testid="open-portal-login">
              Open Portal Login <ArrowRight size={14} className="ml-2" />
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default SportsLanding;
